<template>
    <div>
        <div v-if="noData" class="no_data">
            <p>A configuração de preferências aparecerão ao adicionar turmas e salas.</p>
        </div>
        <div v-else>
            <div class="bulk-toolbar">
                <div class="bulk-toolbar__summary">
                    <strong>Edição em lote</strong>
                    <span>
                        {{ selectedRows.length }} {{ bulkRowLabel }} e
                        {{ selectedColumns.length }} {{ bulkColumnLabel }} selecionados
                    </span>
                </div>

                <div class="bulk-toolbar__actions">
                    <v-select
                        class="bulk-toolbar__select"
                        outlined
                        dense
                        hide-details
                        label="Prioridade"
                        :items="preferenceLevelOptions"
                        item-text="text"
                        item-value="value"
                        v-model="bulkValue"
                    ></v-select>
                    <v-btn
                        small
                        color="primary"
                        :disabled="!canApplyBulk"
                        @click="applyBulkValue"
                    >
                        Aplicar
                    </v-btn>
                    <v-btn
                        small
                        text
                        :disabled="!hasBulkSelection"
                        @click="clearBulkSelection"
                    >
                        Limpar
                    </v-btn>
                </div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th class="selection-column">
                            <v-checkbox
                                class="selection-checkbox"
                                dense
                                hide-details
                                :input-value="allRowsSelected"
                                :indeterminate="someRowsSelected && !allRowsSelected"
                                @change="toggleAllRows"
                            ></v-checkbox>
                        </th>
                        <th></th>
                        <th
                            v-for="(label, idx) in labels"
                            :key="idx"
                            class="column-header"
                        >
                            <v-checkbox
                                class="selection-checkbox"
                                dense
                                hide-details
                                :input-value="isColumnSelected(label)"
                                @change="selected => toggleColumn(label, selected)"
                            ></v-checkbox>
                            <span class="vertical-text">{{ label }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(line, idx) in cells" :key="idx" class="line">
                        <td class="selection-column">
                            <v-checkbox
                                class="selection-checkbox"
                                dense
                                hide-details
                                :input-value="isRowSelected(idx)"
                                @change="selected => toggleRow(idx, selected)"
                            ></v-checkbox>
                        </td>
                        <td class="row-label">{{ line.name.toUpperCase() }}</td>
                        <!-- <p>{{ line }}</p> -->

                        <td
                            v-for="(col, i) in line.data "
                            :key="i"
                            :class="{
                                'bulk-selected': isRowSelected(idx) && isColumnSelected(col.name)
                            }"
                            @mousedown="startDrag(idx, i)"
                            @mousemove="handleDrag"
                            @mouseup="stopDrag"
                            :ref="`cellRefs_${idx}_${i}`"
                        >
                            <v-select class="field" outlined dense :items="preferenceLevelOptions" item-text="text"
                                item-value="value" :value="getPreferenceSelectValue(col.value)"
                                @change="value => handleChange(value, line, col)"></v-select>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
 
<script>
import {
    preferenceLevelOptions,
    getClosestPreferenceLevelValue,
} from "@/utils/PreferenceLevels";

export default {
    name: "TabSetPreferences",
    props: {
        preferences: {
            type: Array,
            default: () => []
        },
        labels: {
            type: Array,
            default: () => []
        },
        noData: {
            type: Boolean,
            default: false
        },
        bulkRowLabel: {
            type: String,
            default: "linhas"
        },
        bulkColumnLabel: {
            type: String,
            default: "colunas"
        },
    },
    watch: {
        preferences: function (_new) {
            this.updateCells(_new)
        },
        labels: function () {
            this.updateCells(this.preferences)
        }
    },
    data() {
        return {
            cells: [],
            selectedCellIndexes: { rowIndex: null, colIndex: null },
            selectedRows: [],
            selectedColumns: [],
            bulkValue: 1,
            preferenceLevelOptions,
        }
    },
    mounted() {
        this.updateCells(this.preferences)
    },
    methods: {
        getPreferenceSelectValue(value) {
            return getClosestPreferenceLevelValue(value);
        },
        handleChange(value, line, col) {
            col.value = Number(value);
            this.emitLineUpdate(line);
        },
        updateCells(_preferences) {

                this.cells = JSON.parse(JSON.stringify(_preferences || [])).map((e) => {
                
                let order = []
                this.labels.forEach((_) => {
                    const data = Array.isArray(e.data) ? e.data : [];
                    order.push(data.find((_e) => _e.name == _) || { name: _, value: 1 })
                })

                e.data = order;

                return {
                    ...e,
                    draggedOver: false,
                    editable: true,
                }
            })
            this.selectedRows = this.selectedRows.filter(rowIndex => rowIndex < this.cells.length)
            this.selectedColumns = this.selectedColumns.filter(column => this.labels.includes(column))
        },
        isRowSelected(rowIndex) {
            return this.selectedRows.includes(rowIndex);
        },
        toggleRow(rowIndex, selected) {
            if (selected && !this.isRowSelected(rowIndex)) {
                this.selectedRows = [...this.selectedRows, rowIndex];
                return;
            }

            if (!selected) {
                this.selectedRows = this.selectedRows.filter(idx => idx !== rowIndex);
            }
        },
        toggleAllRows(selected) {
            this.selectedRows = selected
                ? this.cells.map((_line, index) => index)
                : [];
        },
        isColumnSelected(columnName) {
            return this.selectedColumns.includes(columnName);
        },
        toggleColumn(columnName, selected) {
            if (selected && !this.isColumnSelected(columnName)) {
                this.selectedColumns = [...this.selectedColumns, columnName];
                return;
            }

            if (!selected) {
                this.selectedColumns = this.selectedColumns.filter(name => name !== columnName);
            }
        },
        clearBulkSelection() {
            this.selectedRows = [];
            this.selectedColumns = [];
        },
        applyBulkValue() {
            if (!this.canApplyBulk) {
                return;
            }

            const columnNames = new Set(this.selectedColumns);
            const value = Number(this.bulkValue);

            this.selectedRows.forEach(rowIndex => {
                const line = this.cells[rowIndex];

                if (!line) {
                    return;
                }

                line.data.forEach(cell => {
                    if (columnNames.has(cell.name)) {
                        cell.value = value;
                    }
                });

                this.emitLineUpdate(line);
            });
        },
        emitLineUpdate(line) {
            const updatedPreferences = line.data.map(cell => {
                return {
                    name: cell.name,
                    value: Number(cell.value)
                };
            });

            this.$emit('update', line.idx, updatedPreferences)
        },
        startDrag(rowIndex, colIndex) {
            this.selectedCellIndexes = { rowIndex, colIndex };
        },
        handleDrag(event) {
            const { rowIndex, colIndex } = this.selectedCellIndexes;

            if (rowIndex !== null && colIndex !== null) {
                const selectedCell = this.cells[rowIndex].data[colIndex];
                //const rect = this.$refs.cellRefs[rowIndex][colIndex].getBoundingClientRect();


                const mouseX = event.clientX;
                const mouseY = event.clientY;

                this.cells.forEach((line, i) => {
                    line.data.forEach((cell, j) => {
                        const cellRect = this.$refs[`cellRefs_${i}_${j}`][0].getBoundingClientRect();
                        if (mouseX >= cellRect.left && mouseX <= cellRect.right && mouseY >= cellRect.top && mouseY <= cellRect.bottom) {
                            this.cells[i].data[j].value = selectedCell.value,
                                this.cells[i].data[j].draggedOver = true;
                        } else {
                            this.cells[i].data[j].draggedOver = false;
                        }
                    });
                });
            }
        },
        stopDrag() {
            this.selectedCellIndexes = { rowIndex: null, colIndex: null };

            // Clear the dragged-over property from all cells
            this.cells.forEach((line) => {
                line.data.forEach((cell) => {
                    cell.draggedOver = false;
                });
                this.emitLineUpdate(line)
            });
        },
    },
    computed: {
        someRowsSelected() {
            return this.selectedRows.length > 0;
        },
        allRowsSelected() {
            return this.cells.length > 0 && this.selectedRows.length === this.cells.length;
        },
        hasBulkSelection() {
            return this.selectedRows.length > 0 || this.selectedColumns.length > 0;
        },
        canApplyBulk() {
            return this.selectedRows.length > 0 && this.selectedColumns.length > 0;
        },
    }
};
</script>

<style scoped>
.no_data {
    width: 100%;
    min-height: 200px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.line:hover {
    background-color: #F5F5F5;
}

.bulk-toolbar {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 14px;
}

.bulk-toolbar__summary {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.bulk-toolbar__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.bulk-toolbar__select {
    max-width: 180px;
    min-width: 150px;
}

.selection-column {
    min-width: 44px;
    width: 44px;
}

.selection-checkbox {
    margin: 0;
    padding: 0;
}

.row-label {
    min-width: 220px;
}

.column-header {
    min-width: 150px;
    text-align: center;
}

.vertical-text {
    text-align: right;
    writing-mode: vertical-lr;
    text-orientation: mixed;
}

.without-btn {
    margin-left: 20px;
}

.with-btn {
    margin-left: 70px;
}

.field {
    width: 150px;
    margin-right: 1px;
    height: 40px;
}

.field:hover {
    background-color: #E3F2FD;
}

.bulk-selected {
    background-color: #E3F2FD;
}

.cell {
    border: 1px solid #ccc;
    width: 50px;
    height: 20px;
    text-align: center;
    cursor: pointer;
}

.editable {
    background-color: #e6f7ff;
    /* Light blue background for editable cells */
}

.dragged-over {
    z-index: 2;
    background-color: #64B5F6;
    /* Light red background for cells being dragged over */
}
</style>
